import React from "react";
import styled from "styled-components";
import { FiDownload, FiRotateCcw, FiSearch, FiX } from "react-icons/fi";
import { colors } from "./colors";
import { ENTITY_TYPES } from "./drops";
import { DEFAULT_FILTERS } from "./useDrops";
import { SEVERITY_STYLES, Dot } from "./SeverityBadge";
import FilterSelect from "./FilterSelect";

const SEVERITY_CHIPS = [{ value: "all", label: "All" }, ...Object.entries(SEVERITY_STYLES).map(([value, style]) => ({ value, label: style.label, dot: style.dot }))];

export default function Filters({ filters, causeOptions, severityCounts, hasActiveFilters, onChange, onReset, onExport, exportDisabled }) {
    return (
        <Bar>
            <Chips role="group" aria-label="Filter by severity">
                {SEVERITY_CHIPS.map((chip) => {
                    const active = filters.severity === chip.value;
                    return (
                        <Chip key={chip.value} type="button" aria-pressed={active} $active={active} onClick={() => onChange("severity", chip.value)}>
                            {chip.dot && <Dot $color={chip.dot} />}
                            {chip.label}
                            <Count $active={active}>{severityCounts[chip.value]}</Count>
                        </Chip>
                    );
                })}
            </Chips>
            <Tools>
                <Search>
                    <SearchIcon aria-hidden />
                    <SearchInput type="search" aria-label="Search drops" value={filters.query} placeholder="Search URL, query, cause…"
                                 onChange={(event) => onChange("query", event.target.value)} />
                    {filters.query && <Clear type="button" aria-label="Clear search" onClick={() => onChange("query", "")}><FiX /></Clear>}
                </Search>
                <FilterSelect label="Type" value={filters.type} options={ENTITY_TYPES} onChange={(value) => onChange("type", value)} isDefault={filters.type === DEFAULT_FILTERS.type} />
                <FilterSelect label="Likely cause" value={filters.cause} options={causeOptions} onChange={(value) => onChange("cause", value)} isDefault={filters.cause === DEFAULT_FILTERS.cause} />
                {hasActiveFilters && <TextButton type="button" onClick={onReset}><FiRotateCcw aria-hidden />Reset</TextButton>}
                <Divider aria-hidden />
                <OutlineButton type="button" onClick={onExport} disabled={exportDisabled}><FiDownload aria-hidden />Export CSV</OutlineButton>
            </Tools>
        </Bar>
    );
}

const Bar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 20px;
    border-bottom: 1px solid ${colors.line};
    @media (min-width: 1280px) { flex-direction: row; align-items: center; }
`;

const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
`;

const Chip = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    white-space: nowrap;
    border-radius: 8px;
    padding: 0 12px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid ${({ $active }) => ($active ? colors.ink : colors.line)};
    background: ${({ $active }) => ($active ? colors.ink : colors.surface)};
    color: ${({ $active }) => ($active ? "#fff" : colors.muted)};
    &:hover { border-color: ${({ $active }) => ($active ? colors.ink : colors.lineStrong)}; color: ${({ $active }) => ($active ? "#fff" : colors.ink)}; }
`;

const Count = styled.span`
    font-variant-numeric: tabular-nums;
    color: ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.7)" : colors.subtle)};
`;

const Tools = styled.div`
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    @media (min-width: 1280px) { justify-content: flex-end; }
`;

const Search = styled.label`
    position: relative;
    display: flex;
    flex: 1;
    align-items: center;
    min-width: 200px;
    margin: 0;
    @media (min-width: 1280px) { max-width: 260px; }
`;

const SearchIcon = styled(FiSearch)`
    position: absolute;
    left: 10px;
    pointer-events: none;
    font-size: 14px;
    color: ${colors.subtle};
`;

const SearchInput = styled.input`
    width: 100%;
    height: 32px;
    border-radius: 8px;
    border: 1px solid ${colors.line};
    background: ${colors.surface};
    padding: 0 28px 0 32px;
    font-size: 13px;
    color: ${colors.ink};
    &::placeholder { color: ${colors.subtle}; }
    &::-webkit-search-cancel-button { display: none; }
    &:hover { border-color: ${colors.lineStrong}; }
    &:focus { outline: none; border-color: ${colors.accent}80; box-shadow: 0 0 0 2px ${colors.accent}33; }
`;

const Clear = styled.button`
    position: absolute;
    right: 6px;
    display: grid;
    place-items: center;
    width: 20px;
    height: 20px;
    border: none;
    background: none;
    color: ${colors.subtle};
    cursor: pointer;
    &:hover { color: ${colors.ink}; }
`;

const TextButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    border: none;
    background: none;
    padding: 0 8px;
    font-size: 13px;
    font-weight: 500;
    color: ${colors.muted};
    cursor: pointer;
    &:hover { color: ${colors.ink}; }
`;

const Divider = styled.span`
    width: 1px;
    height: 20px;
    margin: 0 4px;
    background: ${colors.line};
    @media (max-width: 600px) { display: none; }
`;

export const OutlineButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    white-space: nowrap;
    border-radius: 8px;
    border: 1px solid ${colors.line};
    background: ${colors.surface};
    padding: 0 12px;
    font-size: 13px;
    font-weight: 500;
    color: ${colors.ink};
    cursor: pointer;
    text-decoration: none;
    &:hover { border-color: ${colors.lineStrong}; background: ${colors.canvas}; color: ${colors.ink}; text-decoration: none; }
    &:disabled { cursor: not-allowed; opacity: 0.5; }
`;
